import { createSlice } from "@reduxjs/toolkit";
import axios from "../../axios";
import axiosWithoutInterceptor from "../../axiosWithoutInterceptor";
import getConfig from "../../config";
import { rowsPerPageDefault } from "../../shared/constants";
import { cleanupSessionRefreshTimeout } from "../../shared/sessionTimeoutCleanup";
import { redirectToLoginPage } from "../../shared/sessionUtility";
import { isOneWebDomainRuntime } from "../../shared/Utils";
import { DebugLogger } from "../../utils/DebugLogger";
import { CrossTabCoordinator, DEFAULT_CROSS_TAB_CONFIG } from "../session/CrossTabCoordinator";
import transformGrants from "./transformGrants";

// Config is now retrieved lazily inside functions to support runtime configuration
// This ensures config is loaded before being accessed (build once, deploy many strategy)

// Initialize cross-tab coordinator
const crossTabCoordinator = CrossTabCoordinator.getInstance(DEFAULT_CROSS_TAB_CONFIG);

// Configuration
const CONFIG = {
    RETRY: {
        MAX_ATTEMPTS: 3,
        INITIAL_DELAY: 1000,
        MAX_DELAY: 5000,
    },
    REFRESH: {
        BUFFER_TIME: 3 * 60 * 1000, // 3 minutes before expiry
        MIN_REFRESH_INTERVAL: 60 * 1000, // Minimum 1 minute between refreshes
    },
};

export const sessionSlice = createSlice({
    name: "session",
    initialState: {
        isLoading: false,
        isAuthenticated: false,
        provisionStatus: null,
        isInternalUser: false,
        accountId: null,
        userId: null,
        role: null,
        username: null,
        firstName: null,
        lastName: null,
        email: null,
        companyIds: [],
        activeCompany: {
            id: null,
            name: null,
        },
        subscriptions: [],
        environment: null,
        avaTaxAccountId: null,
        avaTaxUserId: null,
        accessToken: null,
        accessTokenExpiresAt: 0,
        accessTokenIssuedAt: 0,
        refreshInSeconds: 0,
        expiresInSeconds: 0,
        isAdminUser: false,
        ecmRole: [],
        iamdsRoles: [],
        rowsPerPage: rowsPerPageDefault,
        transformedGrants: {},
        isIAM: false,
        /**
         * Multi-tax eligibility flag for the active company. Derived from
         * TPS `nexus/TaxTypeDetails` on the WebAPI side and surfaced via
         * `/session`. Default false keeps the deployed SUT-only UI exactly
         * the same when the flag is not present (e.g. older WebAPI builds).
         */
        isMultiTax: false,
        hasSessionLoadError: false,
    },
    reducers: {
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setSessionLoadError: (state, action) => {
            state.hasSessionLoadError = action.payload;
        },
        setData: (state, action) => {
            state.isAuthenticated = action.payload.isAuthenticated;
            state.isInternalUser = action.payload.isInternalUser;
            state.provisionStatus = action.payload.status;
            state.accountId = action.payload.accountId;
            state.userId = action.payload.userId;
            state.role = action.payload.role;
            state.username = action.payload.username;
            state.firstName = action.payload.firstName;
            state.lastName = action.payload.lastName;
            state.email = action.payload.email;
            state.companyIds = action.payload.companyIds;
            state.subscriptions = action.payload.subscriptions;
            state.environment = action.payload.environment;
            state.accessToken = action.payload.accessToken;
            state.accessTokenExpiresAt = action.payload.accessTokenExpiresAt;

            if (action.payload.accessTokenIssuedAt !== undefined) {
                state.accessTokenIssuedAt = action.payload.accessTokenIssuedAt;
            }
            if (action.payload.refreshInSeconds !== undefined) {
                state.refreshInSeconds = action.payload.refreshInSeconds;
            }
            if (action.payload.expiresInSeconds !== undefined) {
                state.expiresInSeconds = action.payload.expiresInSeconds;
            }

            state.isAdminUser = action.payload.role?.includes("Admin");
            state.ecmRole = action.payload.ecmRole ?? [];
            state.iamdsRoles = action.payload.iamdsRoles ?? [];
            state.rowsPerPage = action.payload.rowsPerPage;
            state.scsAvataxCompanyMapping = action.payload.scsAvataxCompanyMapping;
            // `isMultiTax` may be undefined when running against older
            // WebAPI builds — coerce to false so the existing SUT-only UI
            // continues to render unchanged.
            state.isMultiTax = action.payload.isMultiTax === true;

            if (action.payload.activeCompany) {
                state.activeCompany.id = action.payload.activeCompany.id;
                state.activeCompany.name = action.payload.activeCompany.name;
            }

            if (typeof FS !== "undefined") {
                // eslint-disable-next-line no-undef
                FS.identify(action.payload.userId, {
                    displayName: action.payload.username,
                    email: action.payload.email,
                    // id: avalaraSystemUserId,
                    accountId: action.payload.accountId,
                });
            }
            const tgrtPermissions = action.payload?.tgrtPermissions;
            if (tgrtPermissions?.length > 0) {
                // process grants
                state.transformedGrants = transformGrants(
                    tgrtPermissions,
                    action.payload.scsAvataxCompanyMapping?.find(
                        company => company.avaTaxCompanyId === action.payload.activeCompany.id
                    )
                );
                state.isIAM = true;
            }
        },

        setSubscriptions: (state, action) => {
            state.subscriptions = action.payload;
        },
        setRowsPerPage: (state, action) => {
            state.rowsPerPage = action.payload;
        },

        /**
         * Updates only token-related fields during background refresh.
         *
         * This reducer is designed to minimize component re-renders by updating
         * only the fields that change during a background session refresh.
         * Unlike setData, it doesn't update user profile data, company info, etc.
         *
         * Use this for:
         * - Automatic background session refreshes
         * - Token renewal operations
         *
         * Use setData for:
         * - Initial session load
         * - User-initiated refreshes
         * - Company switches
         *
         * @param {Object} action.payload - Session data from API
         */
        updateTokenData: (state, action) => {
            state.accessToken = action.payload.accessToken;
            state.accessTokenExpiresAt = action.payload.accessTokenExpiresAt;
            state.isAuthenticated = action.payload.isAuthenticated;

            if (action.payload.accessTokenIssuedAt !== undefined) {
                state.accessTokenIssuedAt = action.payload.accessTokenIssuedAt;
            }
            if (action.payload.refreshInSeconds !== undefined) {
                state.refreshInSeconds = action.payload.refreshInSeconds;
            }
            if (action.payload.expiresInSeconds !== undefined) {
                state.expiresInSeconds = action.payload.expiresInSeconds;
            }
        },
    },
});

export const {
    setLoading,
    setSessionLoadError,
    setData,
    setSubscriptions,
    setRowsPerPage,
    setValue,
    updateTokenData,
} = sessionSlice.actions;

/**
 * Selector that returns only UI-related session fields, excluding token fields.
 *
 * This prevents components from re-rendering when token fields change during
 * background refresh. Token changes should be transparent to the UI since they're
 * only used for API authentication (handled by interceptors).
 *
 * Use this selector in components that need session data but don't need tokens:
 * - User profile components
 * - Navigation components
 * - Permission-checking components
 *
 * DO NOT use this if you actually need the token (rare - usually only interceptors need it)
 */
export const selectSessionUIFields = state => ({
    isLoading: state.session.isLoading,
    hasSessionLoadError: state.session.hasSessionLoadError,
    isAuthenticated: state.session.isAuthenticated,
    hasToken: !!state.session.accessToken, // Boolean flag to check token existence
    provisionStatus: state.session.provisionStatus,
    isInternalUser: state.session.isInternalUser,
    accountId: state.session.accountId,
    userId: state.session.userId,
    role: state.session.role,
    username: state.session.username,
    firstName: state.session.firstName,
    lastName: state.session.lastName,
    email: state.session.email,
    companyIds: state.session.companyIds,
    activeCompany: state.session.activeCompany,
    subscriptions: state.session.subscriptions,
    environment: state.session.environment,
    avaTaxAccountId: state.session.avaTaxAccountId,
    avaTaxUserId: state.session.avaTaxUserId,
    isAdminUser: state.session.isAdminUser,
    ecmRole: state.session.ecmRole,
    rowsPerPage: state.session.rowsPerPage,
    transformedGrants: state.session.transformedGrants,
    isIAM: state.session.isIAM,
    scsAvataxCompanyMapping: state.session.scsAvataxCompanyMapping,
    isMultiTax: state.session.isMultiTax,
    // Intentionally EXCLUDE token fields to prevent re-renders during background refresh:
    // - accessToken (only needed by interceptors)
    // - accessTokenExpiresAt (only needed for scheduling)
    // - accessTokenIssuedAt (only needed for scheduling)
    // - refreshInSeconds (only needed for scheduling)
    // - expiresInSeconds (only needed for scheduling)
    //
    // Note: We include 'hasToken' as a boolean flag. This will only change on
    // initial login (undefined -> true) or logout (true -> undefined), NOT during
    // background refresh (true -> true with different token value).
});

export const initializeSession = data => dispatch => {
    DebugLogger.log(
        "Session initializing with new data",
        {
            isAuthenticated: data?.isAuthenticated,
            accessTokenExpiresAt: data?.accessTokenExpiresAt
                ? new Date(data.accessTokenExpiresAt * 1000).toISOString()
                : null,
            accessTokenIssuedAt: data?.accessTokenIssuedAt
                ? new Date(data.accessTokenIssuedAt * 1000).toISOString()
                : null,
            refreshInSeconds: data?.refreshInSeconds,
            expiresInSeconds: data?.expiresInSeconds,
            activeCompany: data?.activeCompany?.id,
        },
        "CrossTab"
    );

    // Update Redux state with new session data
    dispatch(setData(data));

    // Schedule next refresh using 3-tier fallback logic
    scheduleRefreshFromData(dispatch, data);
};

/**
 * Forces an immediate session refresh, coordinated across multiple browser tabs.
 *
 * This is the core function that handles session refresh logic with cross-tab
 * coordination to prevent duplicate API calls and cascading failures. It uses
 * a dual-axios approach.
 *
 * Cross-Tab Coordination Strategy:
 * 1. Uses CrossTabCoordinator to ensure only one tab refreshes at a time
 * 2. Leverages shared cookies across tabs for transparent session updates
 * 3. Records successful refreshes so other tabs can skip duplicate attempts
 *
 * Dual-Axios Approach:
 * - Primary: axiosWithoutInterceptor (bypasses error boundaries)
 * - Fallback: regular axios (includes full error handling)
 *
 * This approach solves the original problem where stale Redux state in inactive
 * tabs would trigger error boundaries, causing hard page refreshes across all tabs.
 *
 * Algorithm:
 * 1. Check if refresh is already in progress (prevent duplicate calls)
 * 2. Acquire cross-tab coordination lock (prevent multiple tabs refreshing)
 * 3. Attempt primary refresh with axiosWithoutInterceptor
 * 4. On non-401 errors, fallback to regular axios
 * 5. Handle authentication failures with coordinated redirects
 * 6. Always release locks in finally block
 *
 * Error Handling Strategy:
 * - 401 Unauthorized: Immediate redirect to login page (session expired)
 * - Network/timeout errors: Try fallback axios with full error handling
 * - All other errors: Initialize empty session (graceful degradation)
 *
 * Performance Optimization:
 * - Background refreshes use updateTokenData (lightweight, minimal re-renders)
 * - User-initiated refreshes use initializeSession (full state update)
 *
 * @param {boolean} isBackgroundRefresh - True for automatic refreshes, false for user-initiated
 * @param {boolean} bypassMinInterval - True to bypass minimum interval check (for server-requested immediate refreshes)
 * @returns {Function} Redux thunk function
 */
export const forceRefreshSessionAsync =
    (isBackgroundRefresh = true, bypassMinInterval = false) =>
    async (dispatch, getState) => {
        const { session } = getState();

        // Explicit logging to debug which path is taken
        DebugLogger.log(
            "forceRefreshSessionAsync called",
            { isBackgroundRefresh: isBackgroundRefresh, bypassMinInterval: bypassMinInterval },
            "CrossTab"
        );

        // Guard 1: Prevent concurrent refresh attempts within the same tab
        if (session.isLoading) {
            DebugLogger.log("Session refresh already in progress, skipping", undefined, "CrossTab");
            return;
        }

        // Guard 2: Cross-tab coordination - only one tab should refresh at a time
        if (!crossTabCoordinator.shouldRefresh(bypassMinInterval)) {
            DebugLogger.log(
                "Coordination: delegating to another tab (lock held or recent refresh)",
                undefined,
                "CrossTab"
            );
            return;
        }

        DebugLogger.log(
            "Coordination: acquired refresh lock, proceeding with session refresh",
            undefined,
            "CrossTab"
        );

        // Set loading state to prevent additional refresh attempts
        // Only set loading for user-initiated refreshes to avoid triggering UI re-renders
        if (!isBackgroundRefresh) {
            dispatch(setLoading(true));
        }

        try {
            // Primary Strategy: Use axiosWithoutInterceptor to prevent error boundary triggers
            //
            // This bypasses axios interceptors that could trigger
            // error boundaries on 401/403 responses. Since cookies are shared across tabs,
            // even tabs with stale Redux state will get fresh session data transparently.
            const response = await axiosWithoutInterceptor.get(
                `//${getConfig().hostnames.ecmApiHost}/api/v3/session`,
                {
                    withCredentials: true,
                    timeout: 5000,
                }
            );

            if (!response?.data?.isAuthenticated) {
                DebugLogger.log(
                    "Session not authenticated, redirecting to login page",
                    undefined,
                    "CrossTab"
                );
                // Session is invalid - redirect to login page with cross-tab coordination
                redirectToLoginPage();
                return;
            }

            // Success: Update Redux state with fresh session data
            if (isBackgroundRefresh) {
                // Background refresh: Only update token data to prevent unnecessary re-renders
                DebugLogger.log(
                    "Background refresh: updating token data only",
                    {
                        accessToken: response?.data?.accessToken ? "***" : null,
                        accessTokenExpiresAt: response?.data?.accessTokenExpiresAt
                            ? new Date(response.data.accessTokenExpiresAt * 1000).toISOString()
                            : null,
                        accessTokenIssuedAt: response?.data?.accessTokenIssuedAt
                            ? new Date(response.data.accessTokenIssuedAt * 1000).toISOString()
                            : null,
                        refreshInSeconds: response?.data?.refreshInSeconds,
                        expiresInSeconds: response?.data?.expiresInSeconds,
                    },
                    "CrossTab"
                );
                dispatch(updateTokenData(response?.data));

                // Re-schedule next refresh using 3-tier fallback logic
                scheduleRefreshFromData(dispatch, response.data);
            } else {
                // User-initiated refresh: Update full session data
                DebugLogger.log(
                    "User-initiated refresh: updating full session data",
                    undefined,
                    "CrossTab"
                );
                dispatch(initializeSession(response?.data));
            }

            // Record successful refresh for cross-tab coordination
            // This prevents other tabs from making redundant refresh calls
            crossTabCoordinator.recordSuccessfulRefresh();
        } catch (error) {
            // Error Handling: Differentiate between authentication and other errors

            // Authentication Error: Handle 401 immediately without fallback
            if (error.response?.status === 401) {
                DebugLogger.log(
                    "Session primary request returned 401, redirecting to login page",
                    undefined,
                    "CrossTab"
                );
                // Session expired - redirect to login page with cross-tab coordination
                redirectToLoginPage();
                return;
            }

            // Non-Authentication Errors: Try fallback with regular axios
            // This handles network issues, timeouts, or other transient errors
            try {
                // Fallback Strategy: Use regular axios with full interceptor chain
                const fallbackResponse = await axios.get(
                    `//${getConfig().hostnames.ecmApiHost}/api/v3/session`,
                    {
                        withCredentials: true,
                        timeout: 5000,
                    }
                );

                if (!fallbackResponse?.data?.isAuthenticated) {
                    DebugLogger.log(
                        "Session fallback request not authenticated, redirecting to login page",
                        undefined,
                        "CrossTab"
                    );
                    redirectToLoginPage();
                    return;
                }

                // Fallback Success: Update Redux state
                if (isBackgroundRefresh) {
                    // Background refresh: Only update token data to prevent unnecessary re-renders
                    DebugLogger.log(
                        "Background refresh (fallback): updating token data only",
                        {
                            accessToken: fallbackResponse?.data?.accessToken ? "***" : null,
                            accessTokenExpiresAt: fallbackResponse?.data?.accessTokenExpiresAt
                                ? new Date(
                                      fallbackResponse.data.accessTokenExpiresAt * 1000
                                  ).toISOString()
                                : null,
                            accessTokenIssuedAt: fallbackResponse?.data?.accessTokenIssuedAt
                                ? new Date(
                                      fallbackResponse.data.accessTokenIssuedAt * 1000
                                  ).toISOString()
                                : null,
                            refreshInSeconds: fallbackResponse?.data?.refreshInSeconds,
                            expiresInSeconds: fallbackResponse?.data?.expiresInSeconds,
                        },
                        "CrossTab"
                    );
                    dispatch(updateTokenData(fallbackResponse?.data));

                    // Re-schedule next refresh using 3-tier fallback logic
                    scheduleRefreshFromData(dispatch, fallbackResponse.data);
                } else {
                    // User-initiated refresh: Update full session data
                    DebugLogger.log(
                        "User-initiated refresh (fallback): updating full session data",
                        undefined,
                        "CrossTab"
                    );
                    dispatch(initializeSession(fallbackResponse?.data));
                }

                // Record successful fallback refresh for cross-tab coordination
                crossTabCoordinator.recordSuccessfulRefresh();
            } catch (fallbackError) {
                // Fallback Error Handling

                // Authentication Error in Fallback: Redirect to login
                if (fallbackError.response?.status === 401) {
                    DebugLogger.log(
                        "Session fallback request returned 401, redirecting to login page",
                        undefined,
                        "CrossTab"
                    );
                    redirectToLoginPage();
                    return;
                }

                // Network or Other Errors: Graceful degradation
                // Initialize empty session to prevent app crashes
                // The user will be prompted to login on their next action
                dispatch(initializeSession({}));
            }
        } finally {
            // Critical Cleanup: Always release locks and loading state
            // This ensures other tabs can attempt refreshes if needed
            // and prevents the UI from being stuck in loading state
            crossTabCoordinator.releaseRefreshLock();
            // Only clear loading for user-initiated refreshes (matching the setLoading(true) condition)
            if (!isBackgroundRefresh) {
                dispatch(setLoading(false));
            }
        }
    };

/**
 * Schedules the next session refresh based on available timing data.
 *
 * This function implements a 3-tier fallback strategy:
 * 1. Server-provided timing (refreshInSeconds) - PREFERRED, clock-skew immune
 * 2. Server timestamps (accessTokenIssuedAt + expiresInSeconds) - 20% buffer
 * 3. Legacy calculation (accessTokenExpiresAt) - Current behavior
 *
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} data - Session data from API
 */
const scheduleRefreshFromData = (dispatch, data) => {
    // AvaApp issues short-lived rolling tokens via /api/token/exemptions. ECM's
    // timer-based refresh (refreshInSeconds / 20% buffer) re-triggers almost
    // immediately and cascades into 401s. Token renewal is handled per-request
    // by avaAppToken interceptors instead.
    if (isOneWebDomainRuntime()) {
        cleanupSessionRefreshTimeout();
        DebugLogger.log(
            "Skipping session refresh scheduling in AvaApp runtime",
            undefined,
            "CrossTab"
        );
        return;
    }

    // Tier 1: Use server-provided refreshInSeconds (PREFERRED - clock-skew immune)
    if (data?.refreshInSeconds !== undefined) {
        scheduleNextRefreshWithDelay(dispatch, data);
        return;
    }

    // Tier 2: Calculate from accessTokenIssuedAt + expiresInSeconds (20% buffer)
    if (data?.accessTokenIssuedAt && data?.expiresInSeconds) {
        const sessionStartTime = data.accessTokenIssuedAt * 1000; // Convert to ms
        const tokenDuration = data.expiresInSeconds * 1000; // Convert to ms
        const expirationTime = sessionStartTime + tokenDuration;

        // Calculate 20% buffer (min 2 min, max 15 min)
        const bufferTime = Math.min(Math.max(tokenDuration * 0.2, 2 * 60 * 1000), 15 * 60 * 1000);
        const refreshTime = expirationTime - bufferTime;
        const refreshDelay = Math.max(refreshTime - Date.now(), 0);

        DebugLogger.log(
            "Session refresh scheduling (Tier 2: accessTokenIssuedAt + expiresInSeconds)",
            {
                issuedAt: new Date(sessionStartTime).toISOString(),
                expiresInSeconds: data.expiresInSeconds,
                expiresAt: new Date(expirationTime).toISOString(),
                bufferSeconds: Math.round(bufferTime / 1000),
                refreshInSeconds: Math.round(refreshDelay / 1000),
                refreshAt: new Date(refreshTime).toISOString(),
            },
            "CrossTab"
        );

        scheduleRefreshTimeout(dispatch, refreshDelay, expirationTime);
        return;
    }

    // Tier 3: Legacy fallback using accessTokenExpiresAt
    if (data?.accessTokenExpiresAt) {
        scheduleNextRefreshLegacy(dispatch, data.accessTokenExpiresAt);
        return;
    }

    // No timing information available
    DebugLogger.log(
        "Session no timing information available, skipping refresh scheduling",
        undefined,
        "CrossTab"
    );
};

/**
 * Validates and extracts refresh delay from server-provided timing data.
 *
 * @param {Object} data - Session data with refreshInSeconds and expiresInSeconds
 * @returns {number} Delay in milliseconds
 */
const getRefreshDelay = data => {
    const { refreshInSeconds, expiresInSeconds } = data;

    // Validate server-provided values
    if (refreshInSeconds < 0) {
        DebugLogger.log(
            "Warning: refreshInSeconds is negative, using immediate refresh",
            { refreshInSeconds },
            "CrossTab"
        );
        return 0;
    }

    if (refreshInSeconds >= expiresInSeconds) {
        DebugLogger.log(
            "Warning: refreshInSeconds >= expiresInSeconds, using 80% of expiration",
            { refreshInSeconds, expiresInSeconds },
            "CrossTab"
        );
        return Math.floor(expiresInSeconds * 0.8 * 1000);
    }

    // Convert seconds to milliseconds
    return refreshInSeconds * 1000;
};

/**
 * Schedules next refresh using server-provided timing (Tier 1 - PREFERRED).
 * This is clock-skew immune as the server calculates the timing.
 *
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} data - Session data with refreshInSeconds and expiresInSeconds
 */
const scheduleNextRefreshWithDelay = (dispatch, data) => {
    // Clear any existing timeout
    cleanupSessionRefreshTimeout();

    const refreshDelay = getRefreshDelay(data);
    const refreshTime = Date.now() + refreshDelay;
    const expirationTime = Date.now() + data.expiresInSeconds * 1000;

    DebugLogger.log(
        "Session refresh scheduling (Tier 1: server-provided timing - clock-skew immune)",
        {
            refreshInSeconds: data.refreshInSeconds,
            expiresInSeconds: data.expiresInSeconds,
            refreshInMinutes: Math.round(data.refreshInSeconds / 60),
            expiresInMinutes: Math.round(data.expiresInSeconds / 60),
            bufferPercentage:
                Math.round(
                    ((data.expiresInSeconds - data.refreshInSeconds) / data.expiresInSeconds) * 100
                ) + "%",
            refreshAt: new Date(refreshTime).toISOString(),
            expiresAt: new Date(expirationTime).toISOString(),
        },
        "CrossTab"
    );

    scheduleRefreshTimeout(dispatch, refreshDelay, expirationTime);
};

/**
 * Legacy fallback for scheduling refresh using client-side calculation (Tier 3).
 * Uses the current behavior with CONFIG.REFRESH.BUFFER_TIME.
 *
 * @param {Function} dispatch - Redux dispatch function
 * @param {number} expiresAt - Unix timestamp (seconds) when token expires
 */
const scheduleNextRefreshLegacy = (dispatch, expiresAt) => {
    if (!expiresAt) {
        DebugLogger.log(
            "Session no expiresAt provided, skipping refresh scheduling",
            undefined,
            "CrossTab"
        );
        return;
    }

    // Clear any existing timeout
    cleanupSessionRefreshTimeout();

    const currentTime = Date.now();
    const expirationTime = expiresAt * 1000;
    const timeUntilExpiry = expirationTime - currentTime;
    const refreshDelay = Math.max(timeUntilExpiry - CONFIG.REFRESH.BUFFER_TIME, 0);

    DebugLogger.log(
        "Session refresh scheduling (Tier 3: legacy calculation)",
        {
            expiresAt: new Date(expirationTime).toISOString(),
            bufferTimeSeconds: CONFIG.REFRESH.BUFFER_TIME / 1000,
            refreshInSeconds: Math.round(refreshDelay / 1000),
            refreshAt: new Date(Date.now() + refreshDelay).toISOString(),
        },
        "CrossTab"
    );

    scheduleRefreshTimeout(dispatch, refreshDelay, expirationTime);
};

/**
 * Schedules the actual setTimeout for refresh.
 * Handles both immediate (delay = 0) and future refreshes.
 *
 * @param {Function} dispatch - Redux dispatch function
 * @param {number} refreshDelay - Delay in milliseconds
 * @param {number} expirationTime - Expiration time in milliseconds
 */
const scheduleRefreshTimeout = (dispatch, refreshDelay, expirationTime) => {
    if (refreshDelay > 0) {
        // Normal Case: Schedule refresh for future execution
        window.sessionRefreshTimeout = setTimeout(() => {
            DebugLogger.log(
                "Session timeout fired, triggering background refresh",
                undefined,
                "CrossTab"
            );
            dispatch(forceRefreshSessionAsync(true));
        }, refreshDelay);
    } else {
        // Edge Case: Token needs immediate refresh
        DebugLogger.log(
            "Token needs immediate refresh, scheduling for next tick",
            {
                expiresAt: new Date(expirationTime).toISOString(),
            },
            "CrossTab"
        );
        // Use setTimeout(0) for proper cross-tab coordination
        // Pass bypassMinInterval=true for server-requested immediate refreshes
        window.sessionRefreshTimeout = setTimeout(() => {
            DebugLogger.log(
                "Immediate refresh timeout fired, triggering background refresh with bypass",
                undefined,
                "CrossTab"
            );
            dispatch(forceRefreshSessionAsync(true, true)); // isBackgroundRefresh=true, bypassMinInterval=true
        }, 0);
    }
};

export const executeWithRetry = async (dispatch, refresh, attempt = 0) => {
    const sessionEndpoint = refresh ? "session/refresh-subscriptions" : "session";

    try {
        dispatch(setLoading(true));
        const response = await axios.get(
            `//${getConfig().hostnames.ecmApiHost}/api/v3/${sessionEndpoint}`,
            {
                withCredentials: true,
            }
        );
        if (!response?.data?.isAuthenticated) {
            redirectToLoginPage();
            return null;
        }
        dispatch(initializeSession(response?.data));
        return response?.data;
    } catch (error) {
        attempt += 1;
        if (attempt < CONFIG.RETRY.MAX_ATTEMPTS) {
            // Calculate delay with exponential backoff
            const delay = Math.min(
                CONFIG.RETRY.INITIAL_DELAY * 2 ** (attempt - 1),
                CONFIG.RETRY.MAX_DELAY
            );
            await new Promise(resolve => setTimeout(resolve, delay));
            return executeWithRetry(dispatch, refresh, attempt + 1);
        }
        throw error;
    }
};

export const getSessionAsync =
    (refresh = false) =>
    async (dispatch, getState) => {
        const { session } = getState();
        if ((session.isAuthenticated && refresh === false) || session.isLoading) {
            return;
        }
        dispatch(setSessionLoadError(false));
        try {
            await executeWithRetry(dispatch, refresh);
        } catch (error) {
            // All retries in executeWithRetry were exhausted (e.g. a sustained 503).
            // Without this, isLoading stays stuck at true forever and the app hangs
            // on its bootstrap spinner with no way to recover.
            // eslint-disable-next-line no-console
            console.error("Failed to load session", error);
            dispatch(setSessionLoadError(true));
        } finally {
            dispatch(setLoading(false));
        }
    };

export const updateRowCountPreference = preferenceValue => async dispatch => {
    let preferenceValueUpdated;
    try {
        preferenceValueUpdated = JSON.stringify(preferenceValue);
    } catch {
        preferenceValueUpdated = "{}";
    }
    const config = getConfig();
    await axios.post(
        `//${config.hostnames.ecmApiHost}/api/${config.apiVersion3}/ecm-preferences`,
        {
            preference: "rows-per-page",
            preferenceValue: preferenceValueUpdated,
        },
        { withCredentials: true }
    );
    const response = await axios.get(`//${config.hostnames.ecmApiHost}/api/v3/session`, {
        withCredentials: true,
    });
    dispatch(setRowsPerPage(response.data.rowsPerPage));
};

export const selectSession = state => state.session;

export const selectTransformedGrants = state => state.session.transformedGrants;

export const selectIsIAM = state => state.session.isIAM;

/**
 * Selector for the multi-tax eligibility flag derived from
 * TPS `nexus/TaxTypeDetails` per active company. Components rendering the
 * /review form gate their multi-tax surfaces (TaxType tree, per-TaxType
 * jurisdiction pickers) on this; false (default) keeps the deployed
 * sales-and-use-tax-only UI exactly as is.
 */
export const selectIsMultiTax = state => state.session.isMultiTax === true;

/**
 * True once the active company has finished provisioning. Single home for the
 * `"Provisioned"` status literal so callers gate on a semantic boolean instead
 * of duplicating the string comparison.
 */
export const selectIsCompanyProvisioned = state => state.session.provisionStatus === "Provisioned";

export const selectEcmRole = state => state.session.ecmRole;

export const selectRole = state => state.session.role;

export const getProduct = state => {
    const { session } = state;
    return session.subscriptions;
};

// Data for the Skylab header WebComponent.
// SEE: https://skylabsdk.avalara.io/layout/header
export const selectSkylabHeader = state => {
    const config = getConfig();
    const { ecmApiHost, identity, cupHost } = config.hostnames;

    // Read the saved locale from the preferences slice so Skylab's locale
    // switcher shows the ECM-persisted locale as selected.
    const localePreferenceRaw = state.preferences?.preferences?.find(
        p => p.preference === "locale"
    )?.preferenceValue;
    let savedLocale;
    if (localePreferenceRaw) {
        try {
            savedLocale = JSON.parse(localePreferenceRaw);
        } catch {
            savedLocale = localePreferenceRaw;
        }
    }

    const user = JSON.stringify(
        {
            accountId: state.session.accountId,
            givenName: state.session.firstName,
            familyName: state.session.lastName,
            email: state.session.email,
            isLoggedIn: state.session.isAuthenticated,
            isTechSupport: state.session.isInternalUser,
            emitSwitchAccountsNavEvent: !!state.session.isInternalUser,
            ...(savedLocale ? { locale: savedLocale } : {}),
        },
        null,
        2
    );

    return {
        user,
        productName: "Customer Exemptions",
        env: config.appEnv,
        appKey: "certcapture",
        homeUrl: window.location.origin,
        loginUrl: `https://${cupHost}`,
        logoutUrl: `//${ecmApiHost}/api/v3/auth/logout`,
        changePasswordUrl: `https://${identity}/changePassword`,
        switchCompanyUrl: `https://${cupHost}/cup/a/${state.session.accountId}/c/${state.session.activeCompany.id}/companies/select`,
    };
};

export const refreshSessionContextAsync = () => async (dispatch, getState) => {
    const { session } = getState();

    if (session.isLoading) {
        return false;
    }
    try {
        const response = await axiosWithoutInterceptor.post(
            `//${getConfig().hostnames.ecmApiHost}/api/v3/session/context/refresh`,
            null,
            {
                withCredentials: true,
            }
        );

        return response?.data?.refreshed ?? false;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Session context refresh failed", error);
        return false;
    }
};

export default sessionSlice.reducer;
